import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Modal, Alert, Badge } from 'react-bootstrap';
import { FaCalendarAlt, FaClock, FaUser, FaPhone, FaWhatsapp, FaMapMarkerAlt, FaCheckCircle, FaTimes } from 'react-icons/fa';

const BookingSystem = ({ t }) => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [patientDetails, setPatientDetails] = useState({
    name: '',
    age: '',
    phone: '',
    problem: '',
    preferredLocation: 'clinic',
    urgency: 'normal',
    previousTreatment: 'no'
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      const elements = sectionRef.current.querySelectorAll('.fade-in, .scroll-fade-up, .scroll-zoom, .stagger-animation > *');
      elements.forEach(el => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    { id: 'consultation', name: 'Initial Consultation', duration: '45 min' },
    { id: 'physiotherapy', name: 'Physiotherapy Session', duration: '60 min' },
    { id: 'homevisit', name: 'Home Visit', duration: '60 min' },
    { id: 'emergency', name: 'Emergency Consultation', duration: '30 min' },
    { id: 'package', name: 'Package Consultation', duration: '30 min' }
  ];

  const timeSlots = {
    morning: ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30'],
    afternoon: ['14:00', '14:30', '15:00', '15:30', '16:00', '16:30'],
    evening: ['17:00', '17:30', '18:00', '18:30', '19:00', '19:30']
  };

  useEffect(() => {
    if (selectedDate) {
      // Simulate availability - in real app, this would check against database
      const allSlots = [...timeSlots.morning, ...timeSlots.afternoon, ...timeSlots.evening];
      const unavailableSlots = ['10:30', '15:00', '18:30']; // Example booked slots
      const available = allSlots.filter(slot => !unavailableSlots.includes(slot));
      setAvailableSlots(available);
    }
  }, [selectedDate, timeSlots.morning, timeSlots.afternoon, timeSlots.evening]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedTime(''); // Reset time when date changes
  };

  const handleServiceChange = (serviceId) => {
    setSelectedService(serviceId);
    setShowBookingModal(true);
  };

  const handleInputChange = (field, value) => {
    setPatientDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const today = new Date();
    const maxDate = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days from now
    return maxDate.toISOString().split('T')[0];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const generateBookingMessage = () => {
    const service = services.find(s => s.id === selectedService);
    const urgencyEmoji = patientDetails.urgency === 'urgent' ? '🚨 URGENT: ' : '📅 ';
    
    return `${urgencyEmoji}*APPOINTMENT BOOKING REQUEST*

👨‍⚕️ *Service:* ${service?.name} (${service?.duration})

📅 *Date:* ${formatDate(selectedDate)}
⏰ *Time:* ${formatTime(selectedTime)}
📍 *Location:* ${patientDetails.preferredLocation === 'clinic' ? 'Clinic' : 'Home Visit'}

👤 *Patient Details:*
- Name: ${patientDetails.name}
- Age: ${patientDetails.age}
- Phone: ${patientDetails.phone}
- Problem: ${patientDetails.problem}
- Previous Treatment: ${patientDetails.previousTreatment === 'yes' ? 'Yes' : 'No'}

${patientDetails.urgency === 'urgent' ? '⚡ This is an urgent requirement. Please confirm ASAP!' : ''}

Please confirm the appointment availability. Thank you!`;
  };

  const handleBookingSubmit = () => {
    if (!selectedDate || !selectedTime || !patientDetails.name || !patientDetails.phone) {
      alert('Please fill all required fields');
      return;
    }

    const phoneNumber = "919123860262";
    const message = encodeURIComponent(generateBookingMessage());
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${message}`;
    
    // Show confirmation first
    setShowConfirmation(true);
    
    // Then open WhatsApp after a brief delay
    setTimeout(() => {
      window.open(whatsappURL, '_blank');
      setShowBookingModal(false);
      setShowConfirmation(false);
    }, 2000);
  };

  const resetBookingForm = () => {
    setSelectedService('');
    setSelectedDate('');
    setSelectedTime('');
    setPatientDetails({
      name: '',
      age: '',
      phone: '',
      problem: '',
      preferredLocation: 'clinic',
      urgency: 'normal',
      previousTreatment: 'no'
    });
    setShowBookingModal(false);
  };

  const getTimeSlotClass = (slot) => {
    let className = "time-slot-btn";
    if (selectedTime === slot) className += " selected";
    if (!availableSlots.includes(slot)) className += " unavailable";
    return className;
  };

  const quickBookButtons = [
    { service: 'consultation', label: 'Book Consultation', variant: 'primary', icon: FaUser },
    { service: 'physiotherapy', label: 'Book Session', variant: 'success', icon: FaCalendarAlt },
    { service: 'homevisit', label: 'Home Visit', variant: 'info', icon: FaMapMarkerAlt },
    { service: 'emergency', label: 'Emergency', variant: 'danger', icon: FaPhone }
  ];

  return (
    <section id="booking" className="section-padding bg-light" ref={sectionRef}>
      <Container>
        <Row className="justify-content-center text-center mb-5">
          <Col lg={8}>
            <h2 className="section-title scroll-fade-up title-enhance">
              Book Your Appointment
            </h2>
            <p className="section-subtitle scroll-fade-up fade-in-delay-1">
              Quick and easy appointment booking. Get confirmation within 15 minutes via WhatsApp.
            </p>
          </Col>
        </Row>

        {/* Quick Booking Buttons */}
        <Row className="mb-5">
          <Col lg={12}>
            <div className="quick-booking-section scroll-zoom">
              <h4 className="text-center mb-4 animate-bounce-in">⚡ Quick Book Appointment</h4>
              <Row className="justify-content-center stagger-animation">
                {quickBookButtons.map((btn, index) => (
                  <Col lg={3} md={6} key={btn.service} className="mb-3">
                    <Button
                      variant={btn.variant}
                      size="lg"
                      className="w-100 quick-book-btn hover-lift animate-bounce-in"
                      style={{'--i': index}}
                      onClick={() => handleServiceChange(btn.service)}
                    >
                      <btn.icon className="me-2 animate-heartbeat" />
                      {btn.label}
                    </Button>
                  </Col>
                ))}
              </Row>
            </div>
          </Col>
        </Row>

        {/* Services Grid */}
        <Row className="stagger-animation">
          {services.map((service, index) => (
            <Col lg={4} md={6} key={service.id} className="mb-4">
              <Card className="service-booking-card h-100 hover-lift" style={{'--i': index}}>
                <Card.Body className="text-center">
                  <div className="service-icon animate-bounce-in">
                    {service.id === 'consultation' && <FaUser className="text-primary" />}
                    {service.id === 'physiotherapy' && <FaCalendarAlt className="text-success" />}
                    {service.id === 'homevisit' && <FaMapMarkerAlt className="text-info" />}
                    {service.id === 'emergency' && <FaPhone className="text-danger" />}
                    {service.id === 'package' && <FaCheckCircle className="text-warning" />}
                  </div>
                  <h5 className="service-name">{service.name}</h5>
                  <div className="service-meta">
                    <Badge bg="outline-secondary">
                      <FaClock className="me-1" /> {service.duration}
                    </Badge>
                  </div>
                  <Button
                    variant="outline-primary"
                    className="mt-3 w-100 hover-glow"
                    onClick={() => handleServiceChange(service.id)}
                  >
                    Book Now
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Emergency Contact */}
        <Row className="mt-5">
          <Col lg={12}>
            <div className="emergency-contact-section scroll-fade-up text-center bg-danger text-white p-4 rounded animate-glow">
              <h4 className="animate-heartbeat">🚨 Need Immediate Help?</h4>
              <p className="mb-3">For medical emergencies or severe pain, call directly for immediate assistance.</p>
              <div className="emergency-actions">
                <Button 
                  variant="light" 
                  size="lg" 
                  className="me-3 hover-bounce"
                  onClick={() => window.location.href = "tel:+919123860262"}
                >
                  <FaPhone className="me-2" />
                  Call Now: +91 91238 60262
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg"
                  className="hover-lift"
                  onClick={() => handleServiceChange('emergency')}
                >
                  <FaWhatsapp className="me-2" />
                  Emergency WhatsApp
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Booking Modal */}
      <Modal 
        show={showBookingModal} 
        onHide={() => setShowBookingModal(false)}
        size="lg"
        centered
        className="booking-modal"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <FaCalendarAlt className="me-2" />
            Book Appointment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showConfirmation ? (
            <div className="confirmation-section text-center animate-zoom-in">
              <div className="confirmation-icon animate-bounce-in">
                <FaCheckCircle className="text-success" style={{fontSize: '4rem'}} />
              </div>
              <h4 className="text-success mt-3">Booking Request Sent!</h4>
              <p>You'll receive confirmation via WhatsApp within 15 minutes.</p>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Form>
              {/* Service Selection */}
              <Row className="mb-3">
                <Col>
                  <Form.Label><strong>Selected Service</strong></Form.Label>
                  <div className="selected-service-display">
                    {services.find(s => s.id === selectedService)?.name}
                  </div>
                </Col>
              </Row>

              {/* Patient Details */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Full Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your full name"
                      value={patientDetails.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Age *</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Age"
                      value={patientDetails.age}
                      onChange={(e) => handleInputChange('age', e.target.value)}
                      min="1"
                      max="120"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Phone *</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Phone number"
                      value={patientDetails.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Problem Description */}
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Problem/Condition *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Briefly describe your problem or condition"
                      value={patientDetails.problem}
                      onChange={(e) => handleInputChange('problem', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Date Selection */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Preferred Date *</Form.Label>
                    <Form.Control
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      min={getMinDate()}
                      max={getMaxDate()}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Location</Form.Label>
                    <Form.Select
                      value={patientDetails.preferredLocation}
                      onChange={(e) => handleInputChange('preferredLocation', e.target.value)}
                    >
                      <option value="clinic">Clinic Visit</option>
                      <option value="home">Home Visit (+₹200)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Time Slots */}
              {selectedDate && (
                <Row className="mb-3">
                  <Col>
                    <Form.Label><strong>Available Time Slots *</strong></Form.Label>
                    <div className="time-slots-grid">
                      <div className="time-period">
                        <h6>Morning</h6>
                        <div className="time-slots">
                          {timeSlots.morning.map(slot => (
                            <Button
                              key={slot}
                              variant="outline-primary"
                              size="sm"
                              className={getTimeSlotClass(slot)}
                              onClick={() => setSelectedTime(slot)}
                              disabled={!availableSlots.includes(slot)}
                            >
                              {formatTime(slot)}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="time-period">
                        <h6>Afternoon</h6>
                        <div className="time-slots">
                          {timeSlots.afternoon.map(slot => (
                            <Button
                              key={slot}
                              variant="outline-primary"
                              size="sm"
                              className={getTimeSlotClass(slot)}
                              onClick={() => setSelectedTime(slot)}
                              disabled={!availableSlots.includes(slot)}
                            >
                              {formatTime(slot)}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div className="time-period">
                        <h6>Evening</h6>
                        <div className="time-slots">
                          {timeSlots.evening.map(slot => (
                            <Button
                              key={slot}
                              variant="outline-primary"
                              size="sm"
                              className={getTimeSlotClass(slot)}
                              onClick={() => setSelectedTime(slot)}
                              disabled={!availableSlots.includes(slot)}
                            >
                              {formatTime(slot)}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Additional Options */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Urgency Level</Form.Label>
                    <Form.Select
                      value={patientDetails.urgency}
                      onChange={(e) => handleInputChange('urgency', e.target.value)}
                    >
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent (Same Day)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Previous Physiotherapy?</Form.Label>
                    <Form.Select
                      value={patientDetails.previousTreatment}
                      onChange={(e) => handleInputChange('previousTreatment', e.target.value)}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Booking Summary */}
              {selectedDate && selectedTime && (
                <Alert variant="info" className="booking-summary animate-fade-in">
                  <h6><FaCheckCircle className="me-2" />Booking Summary:</h6>
                  <p className="mb-1"><strong>Service:</strong> {services.find(s => s.id === selectedService)?.name}</p>
                  <p className="mb-1"><strong>Date:</strong> {formatDate(selectedDate)}</p>
                  <p className="mb-1"><strong>Time:</strong> {formatTime(selectedTime)}</p>
                  <p className="mb-0"><strong>Location:</strong> {patientDetails.preferredLocation === 'clinic' ? 'Clinic' : 'Home Visit'}</p>
                </Alert>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetBookingForm}>
            <FaTimes className="me-1" /> Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleBookingSubmit}
            disabled={!selectedDate || !selectedTime || !patientDetails.name || !patientDetails.phone}
            className="hover-glow"
          >
            <FaWhatsapp className="me-2" />
            Confirm Booking
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default BookingSystem;